import React from 'react';
import { useIntl } from 'react-intl';
import type { LyricFitStateName } from 'fym-shared/src/music24/story/lyricFit';
import { TypedMemo } from '../../../../../common/types';
import { lyricPanelMessages } from './lyricPanelMessages';
import type { LyricFitView } from './useLyricFit';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the lyric panel; each part uses its own slice of it */
import styles from './LyricLinePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface LyricFitFootProps {
  fit: LyricFitView;
  twoLines: boolean;
}

const BAR_CLASS: Record<LyricFitStateName, string | undefined> = {
  full: styles.barFull,
  shrinks: styles.barShrinks,
  tooLong: styles.barTooLong,
};

const MESSAGE_CLASS: Record<LyricFitStateName, string | undefined> = {
  full: styles.fitFull,
  shrinks: styles.fitShrinks,
  tooLong: styles.fitTooLong,
};

/**
 * The fit meter (design b1, b2, b5.4): the characters, the full-size limit of the theme, a bar in
 * green, amber or red, and what to do. Red warns; it does not block "Make it".
 */
const LyricFitFootComponent: React.FC<LyricFitFootProps> = ({ fit, twoLines }) => {
  const intl = useIntl();
  const message = (() => {
    if (fit.state === 'full') {
      return intl.formatMessage(lyricPanelMessages.fitFull);
    }
    if (fit.state === 'shrinks') {
      return intl.formatMessage(lyricPanelMessages.fitShrinks);
    }
    return intl.formatMessage(twoLines ? lyricPanelMessages.fitTooLongTwo : lyricPanelMessages.fitTooLongOne);
  })();
  return (
    <div className={styles.foot} data-testid="lyric-fit" data-state={fit.state}>
      <div className={styles.fitRow}>
        <span>{intl.formatMessage(lyricPanelMessages.characters, { count: fit.chars })}</span>
        <span className={styles.grow} />
        <span>{intl.formatMessage(lyricPanelMessages.fullLimit, { count: fit.fullChars })}</span>
      </div>
      <div className={styles.meter} aria-hidden="true">
        <i
          className={`${styles.bar} ${BAR_CLASS[fit.state] ?? ''}`}
          style={{ width: `${Math.round(fit.barShare * 100)}%` }}
        />
        <span className={styles.meterMark} style={{ left: `${Math.round(fit.markShare * 100)}%` }} />
      </div>
      <div className={`${styles.fitMessage} ${MESSAGE_CLASS[fit.state] ?? ''}`} role="status">
        {message}
      </div>
    </div>
  );
};

export const LyricFitFoot = TypedMemo(LyricFitFootComponent);
